import DOMPurify from 'dompurify'
import type { MailAttachment, SendMailAttachment } from './api'

export interface ForwardPart extends SendMailAttachment {
  id: string
  sizeBytes: number
  sourceAttachmentId?: string
  missing?: boolean
}

const CID_PATTERN = /^[\x21-\x3B\x3D\x3F-\x7E]{1,200}$/

// The input is serialized, sanitized HTML. Consume whole quoted attributes:
// a '>' inside alt text is not a tag end, and data-src is not the image source.
const IMAGE_TAG = /<img\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi
function imageSource(tag: string) {
  const attributes = /\s+([^\s"'<>/=]+)(?:\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g
  for (const match of tag.matchAll(attributes)) {
    if (match[1].toLowerCase() !== 'src') continue
    const value = (match[3] ?? match[4] ?? match[5] ?? '').replace(
      /&(amp|quot|apos|lt|gt);/g,
      (_, entity: string) => ({ amp: '&', quot: '"', apos: "'", lt: '<', gt: '>' })[entity] ?? '',
    )
    return {
      value,
      replace: (src: string) =>
        `${tag.slice(0, match.index)} src="${escapeMailHtml(src)}"${tag.slice(match.index + match[0].length)}`,
    }
  }
  return null
}

/** Apply at every quote ingress, including persisted drafts and the outbox.
 * A template keeps parsing inert; only CID images may survive into SMTP. */
export function sanitizeForwardHtml(html: string): string {
  const template = document.createElement('template')
  template.innerHTML = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'textarea',
      'select',
      'button',
      'meta',
      'link',
      'svg',
      'style',
      'video',
      'audio',
      'source',
      'picture',
    ],
    FORBID_ATTR: ['style', 'srcset', 'background', 'poster'],
  })
  for (const img of template.content.querySelectorAll('img')) {
    if (!/^cid:/i.test(img.getAttribute('src') ?? '')) img.remove()
  }
  return template.innerHTML
}

export function normalizeCid(value: string | null | undefined): string | null {
  if (!value) return null
  const cid = value.trim().replace(/^<|>$/g, '')
  return CID_PATTERN.test(cid) ? cid.toLowerCase() : null
}

export function forwardCid(id: string): string {
  // Attachment IDs are database UUIDs. This independent namespace avoids
  // collisions between repeated names and Content-IDs in the source mail.
  if (!/^[0-9a-f-]{36}$/i.test(id)) throw new Error('Ungültige Anhang-ID')
  return `forward-${id.toLowerCase()}@darkember.invalid`
}

export function escapeMailHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )
}

/** Input HTML must have been sanitized with DOMPurify. This pass only maps
 * image references to parts of this one message; it never fetches URLs. */
export function prepareForwardQuote(
  safeBodyHtml: string | null,
  bodyText: string | null,
  headers: { from: string; subject: string; to: string; date: string },
  attachments: MailAttachment[],
): { html: string; text: string; imageIds: string[]; missingCids: string[] } {
  const byCid = new Map<string, MailAttachment>()
  for (const a of attachments) {
    const cid = normalizeCid(a.contentId)
    if (cid && !byCid.has(cid)) byCid.set(cid, a)
  }
  const imageIds = new Set<string>()
  const missingCids = new Map<string, string>()
  const htmlBody = safeBodyHtml
    ? safeBodyHtml.replace(IMAGE_TAG, (tag) => {
        const image = imageSource(tag)
        const src = image?.value
        if (!src?.toLowerCase().startsWith('cid:')) {
          // Remote images and old data: images stay blocked in a forward.
          // The original reader still displays safe legacy data images.
          return ''
        }
        const original = normalizeCid(src.slice(4))
        const part = original ? byCid.get(original) : undefined
        if (!part?.contentType.toLowerCase().startsWith('image/')) {
          const key = original ?? src.slice(4)
          if (!missingCids.has(key))
            missingCids.set(key, `missing-${missingCids.size + 1}@darkember.invalid`)
          return image?.replace(`cid:${missingCids.get(key)}`) ?? ''
        }
        imageIds.add(part.id)
        return image?.replace(`cid:${forwardCid(part.id)}`) ?? ''
      })
    : `<p>${escapeMailHtml(bodyText ?? '(Kein Text-Inhalt)').replace(/\n/g, '<br>')}</p>`
  const headerHtml = `<div><strong>Weitergeleitete Nachricht · ${escapeMailHtml(headers.from)}</strong><br>${escapeMailHtml(headers.subject)}</div>`
  const headerText = `----- Weitergeleitete Nachricht -----\nVon: ${headers.from}\nDatum: ${headers.date}\nBetreff: ${headers.subject}\nAn: ${headers.to}\n\n`
  return {
    html: `<blockquote>${headerHtml}${htmlBody}</blockquote>`,
    text: headerText + (bodyText ?? '(Kein Text-Inhalt)'),
    imageIds: [...imageIds],
    missingCids: [...missingCids.values()],
  }
}

export function removeForwardImage(html: string, cid: string): string {
  return html.replace(IMAGE_TAG, (tag) => {
    return imageSource(tag)?.value.toLowerCase() === `cid:${cid.toLowerCase()}` ? '' : tag
  })
}

export function renderForwardPreview(html: string, parts: ForwardPart[]): string {
  const byCid = new Map(
    parts
      .filter((a) => a.cid && !a.missing && a.contentBase64)
      .map((a) => [a.cid?.toLowerCase(), a]),
  )
  return html.replace(IMAGE_TAG, (tag) => {
    const image = imageSource(tag)
    if (!image?.value.toLowerCase().startsWith('cid:')) return tag
    const part = byCid.get(image.value.slice(4).toLowerCase())
    if (!part || !/^image\/(png|jpeg|gif|webp)$/i.test(part.contentType))
      return '<span>Eingebettetes Bild nicht verfügbar</span>'
    return image.replace(`data:${part.contentType};base64,${part.contentBase64}`)
  })
}

export function composeForwardHtml(intro: string, quoteHtml: string): string {
  return `<div>${escapeMailHtml(intro).replace(/\n/g, '<br>')}</div>${quoteHtml}`
}
